import { FC, Fragment, useContext, useState } from 'react';
import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import SingleProposalStyles from './SingleProposalStyles';
import { useTranslation } from 'react-i18next';
import ProposalHtl from './ProposalHtl';
import ProposalFlt from './ProposalFlt';
import ProposalDyn from './ProposalDyn';
import { checkAHAVI } from 'utils/utils';
import ProposalPcg from './ProposalPcg';
import ProposalTrf from './ProposalTrf';
import { EditProposalContext } from 'contexts/EditProposaContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import NotesModal from './NotesModal';
import { MainContext } from 'contexts/MainContext';

interface SingleProposalBodyProps {
  proposal: ProposalData;
  template: string;
  sort: string;
}

const SingleProposalBody: FC<SingleProposalBodyProps> = ({ proposal, template, sort }) => {
  const { i18n } = useTranslation();
  const { editMode } = useContext(EditProposalContext);
  const [isEditing, setIsEditing] = useState(false);
  const { proposal: prBase, setProposal: setPrBase } = useContext(MainContext);
  const hasHotels = (product: any) =>
    product.listOfItems.some((item: any) => item.hotels?.length > 0);
  const hasFlights = (product: any) =>
    product.listOfItems.some((item: any) => item.flights?.length > 0);
  const hasTransfers = (product: any) =>
    product.listOfItems.some((item: any) => item.transfers?.length > 0);
  const hasPackages = (product: any) =>
    product.listOfItems.some((item: any) => item.packages?.length > 0);
  const hasTickets = (product: any) =>
    product.listOfItems.some((item: any) => item.sportActivites?.length > 0);

  const getFlightIndex = (product: any) =>
    product.listOfItems.findIndex((item: any) => item.flights?.length > 0);
  const getHotelIndex = (product: any) =>
    product.listOfItems.findIndex((item: any) => item.hotels?.length > 0);
  const getTicketIndex = (product: any) =>
    product.listOfItems.findIndex((item: any) => item.sportActivites?.length > 0);

  const wrClass = (i18n.language === 'he' ? ' pr-rtl' : '') + (' pr-template-' + template);

  const getDisplayOrder = (product: any): number => {
    if (sort === '1') {
      return sortByOrderId(product);
    } else if (sort === '2') {
      return sortByServiceDate(product);
    } else if (sort === '3') {
      const productIndex = proposal.listOfProducts.findIndex((p: any) => p.id === product.id);
      return productIndex;
    } else {
      return 999;
    }
  };

  const sortByOrderId = (product: any) => {
    if (product.listOfItems?.length > 1) {
      const htlIndex = product.listOfItems.findIndex((item: any) => item.hotels?.length > 0);
      if (htlIndex !== -1) {
        return product.listOfItems[htlIndex].hotels[0].displayOrder;
      }
      return 999;
    } else if (product.listOfItems.length === 1) {
      const item = product.listOfItems[0];
      if (
        item.sportActivites?.length > 0 &&
        !item.hotels?.length &&
        item.sportActivites[0].displayOrder !== undefined
      ) {
        return item.sportActivites[0].displayOrder;
      }
      if (item.hotels?.length > 0 && item.hotels[0].displayOrder !== undefined) {
        return item.hotels[0].displayOrder;
      }
      if (item.flights?.length > 0 && item.flights[0].displayOrder !== undefined) {
        return item.flights[0].displayOrder;
      }
      if (item.packages?.length > 0 && item.packages[0].displayOrder !== undefined) {
        return item.packages[0].displayOrder;
      }
      if (item.transfers?.length > 0 && item.transfers[0].displayOrder !== undefined) {
        return item.transfers[0].displayOrder;
      }
      return 999;
    } else {
      return 999;
    }
  };

  const sortByServiceDate = (product: any) => {
    if (product.listOfItems.length > 1) {
      const flight = product.listOfItems.find((item: any) => item.flights?.length > 0);
      if (flight) {
        const checkInDate = flight?.flights?.[0]?.departureDateTime;
        return new Date(checkInDate).getTime();
      }
      return 999;
    } else {
      const flight = product.listOfItems?.[0]?.flights?.[0];
      const hotel = product.listOfItems?.[0]?.hotels?.[0];
      const pac = product.listOfItems?.[0]?.packages?.[0];
      const transfer = product.listOfItems?.[0]?.transfers?.[0];
      const activity = product.listOfItems?.[0]?.sportActivites?.[0];

      if (flight) {
        const checkInDate = flight?.departureDateTime;
        return new Date(checkInDate).getTime();
      } else if (hotel) {
        const checkInDate = hotel?.rooms?.[0]?.startDate;
        return new Date(checkInDate).getTime();
      } else if (pac) {
        const checkInDate = pac?.flights?.[0]?.departureDateTime;
        return new Date(checkInDate).getTime();
      } else if (transfer) {
        const checkInDate = transfer?.pickUpDate;
        return new Date(checkInDate).getTime();
      } else if (activity) {
        const checkInDate = activity?.listOfSportActivityOption.find(
          (option: any) => option.description === 'Date'
        )?.value;

        return new Date(checkInDate).getTime();
      }
      return 999;
    }
  };

  const onSave = (desc: string) => {
    setIsEditing(false);
    const newProposalData = { ...prBase.existingProposal };
    newProposalData.note = desc;
    setPrBase({ ...prBase, existingProposal: newProposalData as ProposalData });
  };
  const listOfProductsSorted =
    proposal.listOfProducts?.slice().sort((a, b) => getDisplayOrder(a) - getDisplayOrder(b)) || [];

  return (
    <div id="pr-body">
      <SingleProposalStyles />{' '}
      <div
        className={
          'pr-header-single pr-flex-between' + (checkAHAVI(proposal.note) ? ' pr-rtl' : ' pr-ltr')
        }
      >
        <div dangerouslySetInnerHTML={{ __html: proposal.note.replace(/\n/g, '<br />') }}></div>

        {editMode && (
          <FontAwesomeIcon icon={faPencil} className="cur-poi" onClick={() => setIsEditing(true)} />
        )}
      </div>
      <div className={'pr-body-content ' + wrClass}>
        {listOfProductsSorted.map((product, index) => (
          <Fragment key={product.id}>
            {hasHotels(product) && !hasFlights(product) && (
              <ProposalHtl
                proposalId={product.id}
                hotels={product.listOfItems[0]?.hotels}
                displayPrice={product.displayPrice}
                showCancellationDate={product.showCancellationDate ?? true}
                cancellationDateDelta={product.cancellationDateDelta ?? 0}
                externalParam_1={proposal.externalParam_1}
              />
            )}
            {hasFlights(product) && !hasHotels(product) && (
              <ProposalFlt
                proposalId={product.id}
                flight={product.listOfItems[0]?.flights[0]}
                currency={product.currency}
                displayPrice={product.displayPrice}
              />
            )}
            {((hasFlights(product) && hasHotels(product)) || hasTickets(product)) && (
              <ProposalDyn
                proposalId={product.id}
                flights={product.listOfItems[getFlightIndex(product)]?.flights}
                hotels={product.listOfItems[getHotelIndex(product)]?.hotels}
                ticket={product.listOfItems[getTicketIndex(product)]?.sportActivites?.[0]}
                displayPrice={product.displayPrice}
                currency={product.currency}
              />
            )}
            {hasPackages(product) && (
              <ProposalPcg proposalId={product.id} packages={product.listOfItems[0]?.packages}
                displayPrice={product.displayPrice}
              />
            )}

            {hasTransfers(product) && (
              <ProposalTrf
                proposalId={product.id}
                transfers={product.listOfItems[0]?.transfers}
                displayPrice={product.displayPrice}
              />
            )}
          </Fragment>
        ))}
      </div>
      {isEditing && (
        <NotesModal close={() => setIsEditing(false)} onSave={onSave} freeText={proposal.note} />
      )}
    </div>
  );
};

export default SingleProposalBody;
